import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Palette, Type, RotateCcw, Moon, Sun } from "lucide-react";
import { queryClient, apiRequest } from "@/lib/queryClient";

// Default theme values matching index.css
const DEFAULT_LIGHT_COLORS = {
  background: "hsl(210, 40%, 98%)",
  foreground: "hsl(210, 15%, 15%)",
  card: "hsl(0, 0%, 100%)",
  cardForeground: "hsl(210, 15%, 15%)",
  primary: "hsl(213, 92%, 48%)",
  primaryForeground: "hsl(0, 0%, 100%)",
  secondary: "hsl(210, 40%, 94%)",
  secondaryForeground: "hsl(210, 15%, 30%)",
  accent: "hsl(25, 95%, 55%)",
  accentForeground: "hsl(0, 0%, 100%)",
  muted: "hsl(210, 40%, 94%)",
  mutedForeground: "hsl(210, 15%, 50%)",
  destructive: "hsl(0, 84%, 60%)",
  destructiveForeground: "hsl(0, 0%, 100%)",
  border: "hsl(210, 40%, 88%)",
  input: "hsl(210, 40%, 88%)",
  ring: "hsl(213, 92%, 48%)",
};

const DEFAULT_DARK_COLORS = {
  background: "hsl(0, 0%, 0%)",
  foreground: "hsl(200, 7%, 91%)",
  card: "hsl(228, 10%, 10%)",
  cardForeground: "hsl(0, 0%, 85%)",
  primary: "hsl(203, 87%, 52%)",
  primaryForeground: "hsl(0, 0%, 100%)",
  secondary: "hsl(195, 15%, 95%)",
  secondaryForeground: "hsl(210, 25%, 8%)",
  accent: "hsl(205, 70%, 8%)",
  accentForeground: "hsl(203, 87%, 52%)",
  muted: "hsl(0, 0%, 9%)",
  mutedForeground: "hsl(210, 3%, 46%)",
  destructive: "hsl(356, 90%, 54%)",
  destructiveForeground: "hsl(0, 0%, 100%)",
  border: "hsl(210, 5%, 15%)",
  input: "hsl(207, 27%, 18%)",
  ring: "hsl(202, 89%, 53%)",
};

const FONT_OPTIONS = [
  { value: "Inter, sans-serif", label: "Inter (Default)" },
  { value: "system-ui, sans-serif", label: "System UI" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "Helvetica, sans-serif", label: "Helvetica" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "Times New Roman, serif", label: "Times New Roman" },
  { value: "Courier New, monospace", label: "Courier New" },
  { value: "Verdana, sans-serif", label: "Verdana" },
  { value: "Tahoma, sans-serif", label: "Tahoma" },
  { value: "Trebuchet MS, sans-serif", label: "Trebuchet MS" },
  { value: "Roboto, sans-serif", label: "Roboto" },
  { value: "Open Sans, sans-serif", label: "Open Sans" },
  { value: "Lato, sans-serif", label: "Lato" },
  { value: "Montserrat, sans-serif", label: "Montserrat" },
  { value: "Poppins, sans-serif", label: "Poppins" },
];

interface ColorInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
}

function ColorInput({ label, value, onChange, description }: ColorInputProps) {
  // Convert HSL to hex for color picker
  const hslToHex = (hsl: string): string => {
    const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (!match) return "#000000";
    
    const h = parseInt(match[1]) / 360;
    const s = parseInt(match[2]) / 100;
    const l = parseInt(match[3]) / 100;
    
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = Math.round(hue2rgb(p, q, h + 1/3) * 255);
    const g = Math.round(hue2rgb(p, q, h) * 255);
    const b = Math.round(hue2rgb(p, q, h - 1/3) * 255);
    
    return "#" + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
  };
  
  // Convert hex to HSL
  const hexToHsl = (hex: string): string => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    
    return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
  };
  
  const hexValue = hslToHex(value);
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={hexValue}
            onChange={(e) => onChange(hexToHsl(e.target.value))}
            className="w-12 h-8 rounded border cursor-pointer"
            data-testid={`color-input-${label.toLowerCase().replace(/\s+/g, '-')}`}
          />
          <code className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
            {value}
          </code>
        </div>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
}

export default function ThemeBuilder() {
  const { toast } = useToast();
  const [activeMode, setActiveMode] = useState<"light" | "dark">("light");
  
  // Fetch user preferences
  const { data: preferences } = useQuery({
    queryKey: ["/api/user/preferences"],
  });
  
  // Initialize theme state from preferences or defaults
  const [lightColors, setLightColors] = useState(DEFAULT_LIGHT_COLORS);
  const [darkColors, setDarkColors] = useState(DEFAULT_DARK_COLORS);
  const [fonts, setFonts] = useState({
    body: "Inter, sans-serif",
    headings: "Inter, sans-serif",
  });
  
  // Load theme from preferences when they load
  useEffect(() => {
    if (preferences) {
      const themeColors = (preferences as any).themeColors;
      const themeFonts = (preferences as any).themeFonts;
      
      if (themeColors) {
        setLightColors(themeColors.light || DEFAULT_LIGHT_COLORS);
        setDarkColors(themeColors.dark || DEFAULT_DARK_COLORS);
      }
      if (themeFonts) {
        setFonts(themeFonts);
      }
    }
  }, [preferences]);
  
  // Apply theme to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const colors = activeMode === "light" ? lightColors : darkColors;
    
    Object.entries(colors).forEach(([key, value]) => {
      const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVar, value);
    });
    
    root.style.setProperty('--font-sans', fonts.body);
  }, [lightColors, darkColors, fonts, activeMode]);
  
  // Save theme mutation
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log("[ThemeBuilder] Saving theme data:", data);
      const response = await apiRequest("PATCH", "/api/user/preferences", data);
      const result = await response.json();
      console.log("[ThemeBuilder] Save response:", result);
      return result;
    },
    onSuccess: (data) => {
      console.log("[ThemeBuilder] Save successful, updating cache with:", data);
      // Update the cache immediately with the returned data
      queryClient.setQueryData(["/api/user/preferences"], data);
      toast({
        title: "Theme Saved",
        description: "Your custom theme has been saved successfully.",
      });
    },
    onError: (error: any) => {
      console.error("[ThemeBuilder] Save error:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to save theme. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  const handleSaveTheme = () => {
    saveMutation.mutate({
      themeColors: {
        light: lightColors,
        dark: darkColors,
      },
      themeFonts: fonts,
    });
  };
  
  const handleResetTheme = () => {
    setLightColors(DEFAULT_LIGHT_COLORS);
    setDarkColors(DEFAULT_DARK_COLORS);
    setFonts({
      body: "Inter, sans-serif",
      headings: "Inter, sans-serif",
    });
    
    toast({
      title: "Theme Reset",
      description: "Theme has been reset to defaults.",
    });
  };
  
  const currentColors = activeMode === "light" ? lightColors : darkColors;
  const setCurrentColors = activeMode === "light" ? setLightColors : setDarkColors;
  
  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Theme Builder</h1>
        <p className="text-muted-foreground">
          Customize colors and fonts to personalize your experience
        </p>
      </div>
      
      <div className="grid gap-6">
        {/* Mode Switcher */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Preview Mode
            </CardTitle>
            <CardDescription>
              Switch between light and dark mode to customize each theme
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button
                variant={activeMode === "light" ? "default" : "outline"}
                onClick={() => setActiveMode("light")}
                className="flex-1"
                data-testid="button-mode-light"
              >
                <Sun className="w-4 h-4 mr-2" />
                Light Mode
              </Button>
              <Button
                variant={activeMode === "dark" ? "default" : "outline"}
                onClick={() => setActiveMode("dark")}
                className="flex-1"
                data-testid="button-mode-dark"
              >
                <Moon className="w-4 h-4 mr-2" />
                Dark Mode
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Theme Customization */}
        <Tabs defaultValue="colors" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="colors" data-testid="tab-colors">
              <Palette className="w-4 h-4 mr-2" />
              Colors
            </TabsTrigger>
            <TabsTrigger value="fonts" data-testid="tab-fonts">
              <Type className="w-4 h-4 mr-2" />
              Fonts
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="colors" className="space-y-4">
            {/* Base Colors */}
            <Card>
              <CardHeader>
                <CardTitle>Base Colors</CardTitle>
                <CardDescription>
                  Main background and text colors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorInput
                  label="Background"
                  value={currentColors.background}
                  onChange={(val) => setCurrentColors({ ...currentColors, background: val })}
                  description="Main page background color"
                />
                <ColorInput
                  label="Foreground"
                  value={currentColors.foreground}
                  onChange={(val) => setCurrentColors({ ...currentColors, foreground: val })}
                  description="Main text color"
                />
                <ColorInput
                  label="Border"
                  value={currentColors.border}
                  onChange={(val) => setCurrentColors({ ...currentColors, border: val })}
                  description="Border and separator colors"
                />
              </CardContent>
            </Card>
            
            {/* UI Elements */}
            <Card>
              <CardHeader>
                <CardTitle>UI Elements</CardTitle>
                <CardDescription>
                  Cards, inputs, and other elements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorInput
                  label="Card Background"
                  value={currentColors.card}
                  onChange={(val) => setCurrentColors({ ...currentColors, card: val })}
                />
                <ColorInput
                  label="Card Text"
                  value={currentColors.cardForeground}
                  onChange={(val) => setCurrentColors({ ...currentColors, cardForeground: val })}
                />
                <ColorInput
                  label="Input Background"
                  value={currentColors.input}
                  onChange={(val) => setCurrentColors({ ...currentColors, input: val })}
                />
                <ColorInput
                  label="Muted Background"
                  value={currentColors.muted}
                  onChange={(val) => setCurrentColors({ ...currentColors, muted: val })}
                />
                <ColorInput
                  label="Muted Text"
                  value={currentColors.mutedForeground}
                  onChange={(val) => setCurrentColors({ ...currentColors, mutedForeground: val })}
                />
              </CardContent>
            </Card>
            
            {/* Brand Colors */}
            <Card>
              <CardHeader>
                <CardTitle>Brand Colors</CardTitle>
                <CardDescription>
                  Primary, secondary, and accent colors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorInput
                  label="Primary"
                  value={currentColors.primary}
                  onChange={(val) => setCurrentColors({ ...currentColors, primary: val })}
                  description="Main brand color for buttons and links"
                />
                <ColorInput
                  label="Primary Text"
                  value={currentColors.primaryForeground}
                  onChange={(val) => setCurrentColors({ ...currentColors, primaryForeground: val })}
                />
                <ColorInput
                  label="Secondary"
                  value={currentColors.secondary}
                  onChange={(val) => setCurrentColors({ ...currentColors, secondary: val })}
                />
                <ColorInput
                  label="Secondary Text"
                  value={currentColors.secondaryForeground}
                  onChange={(val) => setCurrentColors({ ...currentColors, secondaryForeground: val })}
                />
                <ColorInput
                  label="Accent"
                  value={currentColors.accent}
                  onChange={(val) => setCurrentColors({ ...currentColors, accent: val })}
                  description="Highlight and notification colors"
                />
                <ColorInput
                  label="Accent Text"
                  value={currentColors.accentForeground}
                  onChange={(val) => setCurrentColors({ ...currentColors, accentForeground: val })}
                />
              </CardContent>
            </Card>
            
            {/* Status Colors */}
            <Card>
              <CardHeader>
                <CardTitle>Status Colors</CardTitle>
                <CardDescription>
                  Destructive actions and alerts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorInput
                  label="Destructive"
                  value={currentColors.destructive}
                  onChange={(val) => setCurrentColors({ ...currentColors, destructive: val })}
                  description="Error states and delete actions"
                />
                <ColorInput
                  label="Destructive Text"
                  value={currentColors.destructiveForeground}
                  onChange={(val) => setCurrentColors({ ...currentColors, destructiveForeground: val })}
                />
                <ColorInput
                  label="Focus Ring"
                  value={currentColors.ring}
                  onChange={(val) => setCurrentColors({ ...currentColors, ring: val })}
                  description="Keyboard focus indicator"
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="fonts">
            <Card>
              <CardHeader>
                <CardTitle>Typography</CardTitle>
                <CardDescription>
                  Choose fonts for body text and headings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="body-font">Body Font</Label>
                  <Select
                    value={fonts.body}
                    onValueChange={(value) => setFonts({ ...fonts, body: value })}
                  >
                    <SelectTrigger id="body-font" data-testid="select-body-font">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((font) => (
                        <SelectItem key={font.value} value={font.value}>
                          <span style={{ fontFamily: font.value }}>{font.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground" style={{ fontFamily: fonts.body }}>
                    Preview: The quick brown fox jumps over the lazy dog
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="heading-font">Heading Font</Label>
                  <Select
                    value={fonts.headings}
                    onValueChange={(value) => setFonts({ ...fonts, headings: value })}
                  >
                    <SelectTrigger id="heading-font" data-testid="select-heading-font">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((font) => (
                        <SelectItem key={font.value} value={font.value}>
                          <span style={{ fontFamily: font.value }}>{font.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: fonts.headings }}>
                    Preview Heading
                  </h2>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Action Buttons */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleResetTheme}
                data-testid="button-reset-theme"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button
                onClick={handleSaveTheme}
                disabled={saveMutation.isPending}
                data-testid="button-save-theme"
              >
                {saveMutation.isPending ? "Saving..." : "Save Theme"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
